// src/pages/URLGenerator.jsx
import { useState, useMemo } from "react";
import { Link2, PlusCircle, Trash2, Copy, Check } from "lucide-react";

const GOOGLE_ADS_PARAMS = [
  "campaignid","adgroupid","creative","keyword","matchtype",
  "network","device","placement","targetid","feeditemid",
  "loc_physical_ms","loc_interest_ms","random","adposition",
  "extensionid","param1","param2","param3",
];

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none font-mono";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

export default function URLGenerator() {
  const [baseUrl, setBaseUrl] = useState("");
  const [params, setParams] = useState(["campaignid","adgroupid","creative","keyword","matchtype","network","device"]);
  const [copied, setCopied] = useState(false);

  const isValidUrl = useMemo(() => {
    if (!baseUrl) return false;
    try { const u = new URL(baseUrl); return u.protocol === "http:" || u.protocol === "https:"; }
    catch { return false; }
  }, [baseUrl]);

  const addParam    = () => { if (params.length < 10) setParams((p) => [...p, ""]); };
  const removeParam = (i) => setParams((prev) => prev.filter((_, idx) => idx !== i));
  const handleParamChange = (i, value) => setParams((prev) => { const c = [...prev]; c[i] = value; return c; });
  const applyGoogleAdsSuggestion = () => setParams(["campaignid","adgroupid","creative","keyword","matchtype","network","device"]);

  const buildQueryString = (list) =>
    list.filter((p) => typeof p === "string" && p.trim() !== "").map((p) => `${p}={${p}}`).join("&");

  const finalUrl = useMemo(() => {
    const qs = buildQueryString(params);
    if (!qs) return baseUrl || "";
    if (!baseUrl) return `?${qs}`;
    return baseUrl.includes("?") ? `${baseUrl}&${qs}` : `${baseUrl}?${qs}`;
  }, [baseUrl, params]);

  const canCopy = isValidUrl && buildQueryString(params).length > 0;

  const handleCopy = () => {
    if (!canCopy) return;
    navigator.clipboard.writeText(finalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-2xl space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Link2 className="w-6 h-6" /> Gerador de URL Google Ads
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Gere URLs com parâmetros dinâmicos para rastreamento avançado.
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-5">

          {/* URL base */}
          <div>
            <label className={labelCls}>URL da sua Presell</label>
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value.trim())}
              placeholder="https://sua-presell.com/"
              className={inputCls}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              <strong>Dica:</strong> Use apenas parâmetros dinâmicos do Google Ads para rastreamento avançado.{" "}
              <span className="font-mono">campaignid=&#123;campaignid&#125;&amp;keyword=&#123;keyword&#125;</span>
            </p>
            {!isValidUrl && baseUrl.length > 0 && (
              <p className="text-xs text-red-500 mt-1">Informe uma URL válida, começando com http:// ou https://</p>
            )}
            <button type="button" onClick={applyGoogleAdsSuggestion}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-medium">
              <PlusCircle className="w-3.5 h-3.5" /> Usar padrão Google Ads
            </button>
          </div>

          {/* Parâmetros */}
          <div>
            <label className={labelCls}>Parâmetros</label>
            <div className="space-y-2">
              {params.map((param, index) => (
                <div key={index} className="flex items-center gap-2">
                  <select
                    value={param}
                    onChange={(e) => handleParamChange(index, e.target.value)}
                    className={`${inputCls} flex-1`}
                  >
                    <option value="">Selecione um parâmetro</option>
                    {GOOGLE_ADS_PARAMS.filter((opt) => !params.includes(opt) || opt === param).map((opt) => (
                      <option key={opt} value={opt}>{`{${opt}}`}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeParam(index)}
                    className="inline-flex items-center justify-center p-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-3">
              <button onClick={addParam} disabled={params.length >= 10} type="button"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                <PlusCircle className="w-4 h-4" /> Adicionar parâmetro
              </button>
              {params.length >= 10 && <span className="text-xs text-slate-500">Máximo de 10 parâmetros.</span>}
            </div>
          </div>

          {/* URL final */}
          <div>
            <label className={labelCls}>URL Final</label>
            <div className="flex items-center gap-2">
              <input type="text" readOnly value={finalUrl} className={`${inputCls} flex-1 text-xs`} />
              <button onClick={handleCopy} disabled={!canCopy} type="button"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0">
                {copied ? <><Check className="w-4 h-4" /> Copiado!</> : <><Copy className="w-4 h-4" /> Copiar</>}
              </button>
            </div>
            {!buildQueryString(params) && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Adicione pelo menos um parâmetro para gerar a URL.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}